import { useAuth } from "@/context/auth/AuthContext";
import api from "@/lib/axiosConfig";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export type DashboardStats = {
  artworks: number;
  artworksAddedThisMonth: number;
};

export type TutorialItem = { title: string; url: string };
export type TutorialCategory = { title: string; items: TutorialItem[] };

/** Chiffres de la carte « Mes données ». */
export const useDashboardStats = () => {
  const { instanceData } = useAuth();
  return useQuery({
    queryKey: ["dashboard-home", "stats", instanceData?.id],
    queryFn: async () => {
      const { data } = await api.get<DashboardStats>(
        "back/api/instance/dashboard-stats",
      );
      return data;
    },
    enabled: Boolean(instanceData?.id),
  });
};

/** Tutoriels de la page WordPress, rangés par rubrique (lus et mis en cache par l'API). */
/** Tutoriels (galadmin) dans la langue de l'interface, repli français côté API. */
export const useTutorials = (enabled = true) => {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  return useQuery({
    queryKey: ["dashboard-home", "tutorials", locale],
    queryFn: async () => {
      const { data } = await api.get<{ categories: TutorialCategory[] }>(
        "back/api/tutorials",
        { params: { locale } },
      );
      return data.categories;
    },
    enabled,
    // Court : une modification faite dans galadmin doit vite se voir.
    staleTime: 5 * 60 * 1000,
  });
};

const backgroundImagesKey = (instanceId: string | undefined) => [
  "dashboard-home",
  "background-images",
  instanceId,
];

const BACKGROUND_IMAGE_ROUTE =
  "back/api/user-config/dashboard/background-image";

/**
 * URLs signées des trois images personnelles (null = emplacement vide). Elles
 * sont régénérées à chaque appel : on évite les refetch (staleTime < durée de
 * validité d'une heure) pour ne pas recharger l'image, et l'appelant ne
 * mémoïse que sur les chaînes `url`.
 */
export const useDashboardBackgroundImages = (enabled: boolean) => {
  const { instanceData } = useAuth();
  return useQuery({
    queryKey: backgroundImagesKey(instanceData?.id),
    queryFn: async () => {
      const { data } = await api.get<{ urls: (string | null)[] }>(
        `${BACKGROUND_IMAGE_ROUTE}/all`,
      );
      return data.urls;
    },
    enabled: enabled && Boolean(instanceData?.id),
    staleTime: 45 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

/** Met à jour un emplacement dans le cache des trois URL. */
function useSetBackgroundSlot() {
  const queryClient = useQueryClient();
  const { instanceData } = useAuth();
  return (slot: number, url: string | null) =>
    queryClient.setQueryData<(string | null)[]>(
      backgroundImagesKey(instanceData?.id),
      (urls) => {
        const next = [...(urls ?? [null, null, null])];
        next[slot - 1] = url;
        return next;
      },
    );
}

export const useUploadDashboardBackgroundImage = () => {
  const setSlot = useSetBackgroundSlot();
  return useMutation({
    mutationFn: async ({ slot, file }: { slot: number; file: File }) => {
      const formData = new FormData();
      formData.append("image", file);
      const { data } = await api.put<{ url: string }>(
        BACKGROUND_IMAGE_ROUTE,
        formData,
        {
          params: { slot },
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return { slot, url: data.url };
    },
    onSuccess: ({ slot, url }) => setSlot(slot, url),
  });
};

export const useDeleteDashboardBackgroundImage = () => {
  const setSlot = useSetBackgroundSlot();
  return useMutation({
    mutationFn: async (slot: number) => {
      await api.delete(BACKGROUND_IMAGE_ROUTE, { params: { slot } });
      return slot;
    },
    onSuccess: (slot) => setSlot(slot, null),
  });
};
